/**
 * NPEContractListPage — danh sách NPE Contracts.
 * UX/UI đồng bộ ContractListView (TableVirtuoso + popover filter + TablePagination).
 */
import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import BrandState from '@/components/BrandState'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import ConfirmDialog from '@/components/ConfirmDialog'
import ResizeHandle from '@/components/ResizeHandle'
import RoleGuard from '@/components/RoleGuard'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useDeleteNPEContractMutation,
  useGetNPEContractQuery,
  useLazyPrintNPEContractQuery,
  useListNPEContractsQuery,
} from '@/store/api/npeContractsApi'
import type { NPEContractOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { sumNumericColumn } from '@/utils/tableTotals'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import NPEContractFormDialog from './NPEContractFormDialog'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'

type FilterKind = 'search'

const ALL_ROWS_PAGE_SIZE = 1_000_000

const actionToolbarBtnSx: SxProps<Theme> = { minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none' }

type ColumnKind = 'text' | 'date' | 'datetime' | 'number' | 'bool'
type ColumnDef = { key: Extract<keyof NPEContractOut, string>; label: string; kind?: ColumnKind; align?: 'right'; filter?: ColumnFilterKind }
const NON_SUM_TOTAL_KEYS = new Set<string>()

// 35 cột — thứ tự theo spec chị yêu cầu.
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'consignment_p_id', label: 'ID', kind: 'number', align: 'right' },
  { key: 'contract_no', label: 'NPE No.' },
  { key: 'contract_date', label: 'Contract Date', kind: 'date' },
  { key: 'cp_code', label: 'CP Code', filter: 'checkboxes' },
  { key: 'name', label: 'CP Name', filter: 'checkboxes' },
  { key: 'quantity_kg', label: 'Quantity', kind: 'number', align: 'right' },
  { key: 'quality_code', label: 'Quality Code', filter: 'checkboxes' },
  { key: 'certification', label: 'Certification', filter: 'checkboxes' },
  { key: 'package_name', label: 'Package' },
  { key: 'delivery_time', label: 'Delivery Time', kind: 'date' },
  { key: 'delivery_place', label: 'Delivery Place Name' },
  { key: 'shipper_code', label: 'Shipper Code' },
  { key: 'shipper_name', label: 'Shipper Name' },
  { key: 'note', label: 'Note' },
  { key: 'stoploss_rate', label: 'Stoploss Rate', kind: 'number', align: 'right' },
  { key: 'interest_rate_note', label: 'Interest Rate' },
  { key: 'provisional_market_rate', label: 'Provisional Market Rate', kind: 'number', align: 'right' },
  { key: 'storage_charge', label: 'Storage charge', kind: 'number', align: 'right' },
  { key: 'storage_loss', label: 'Storage Loss', kind: 'number', align: 'right' },
  { key: 'two_way_handling_fee', label: 'Handing Fee', kind: 'number', align: 'right' },
  { key: 'insurance_fee', label: 'Insurance Fee', kind: 'number', align: 'right' },
  { key: 'received', label: 'Received (KG)', kind: 'number', align: 'right' },
  { key: 'to_be_receive', label: 'To be Receive (KG)', kind: 'number', align: 'right' },
  { key: 'fixed', label: 'Fixed (KG)', kind: 'number', align: 'right' },
  { key: 'to_be_fix', label: 'To be Fix (KG)', kind: 'number', align: 'right' },
  { key: 'advanced', label: 'Advanced (KG)', kind: 'number', align: 'right' },
  { key: 'to_be_advance', label: 'To be Advance (KG)', kind: 'number', align: 'right' },
  { key: 'paid_vnd', label: 'Paid Amount', kind: 'number', align: 'right' },
  { key: 'allocated_paid', label: 'Allocated to P Paid', kind: 'number', align: 'right' },
  { key: 'signed_by_gm_text', label: 'Signed by GM' },
  { key: 'sent_to_client_text', label: 'Sent to client' },
  { key: 'signed_by_client_text', label: 'Signed by client' },
  { key: 'trader_in_charge_name', label: 'Trader In Charge' },
  { key: 'person_update', label: 'Updated Person' },
  { key: 'date_update', label: 'Updated Date', kind: 'datetime' },
]
// Các cột này không sum được (không phải metric cộng dồn).
const NON_SUM_KEYS_OVERRIDE = new Set<string>([
  'consignment_p_id',
  'stoploss_rate',
  'provisional_market_rate',
  'storage_charge',
  'storage_loss',
  'two_way_handling_fee',
  'insurance_fee',
])

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${`${d.getFullYear()}`}` }
const formatAccessDateTime = (v: unknown): string => {
  if (!v) return ''
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}
const formatAccessNumber = (v: number): string => Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const cellValue = (row: NPEContractOut, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'datetime') return formatAccessDateTime(val)
  if (col.kind === 'bool') return val ? 'Yes' : 'No'
  if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (typeof val === 'number') return formatAccessNumber(val)
  return String(val)
}

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

function buildNPEPrintPreviewHtml(rawHtml: string): string {
  if (!rawHtml) return ''
  if (rawHtml.includes('<base ')) return rawHtml
  return rawHtml.replace('<head>', `<head><base href="${window.location.origin}/">`)
}

type NPEPrintTarget = { contractNo: string }

interface NPEContractPrintDialogProps {
  open: boolean
  printTarget: NPEPrintTarget | null
  onClose: () => void
}

/**
 * Print Preview - NPE Contract.
 * Access parity: `btnPrint_Click` → `DoCmd.OpenReport "rpPurchaseQryNPE"`.
 * Auto-fetches HTML on open, renders in iframe srcDoc — mirrors LocalP
 * "Print Preview - Cancel" pattern; Print button uploads HTML and prints via
 * `printAndUploadReport` (server converts to PDF using same template).
 */
function NPEContractPrintDialog({ open, printTarget, onClose }: NPEContractPrintDialogProps) {
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewContractNo, setPreviewContractNo] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [toastMessage, setToastMessage] = useState('')
  const [triggerPrint, { isFetching }] = useLazyPrintNPEContractQuery()
  const autoPreviewKeyRef = useRef('')

  const contractNo = printTarget?.contractNo ?? ''

  const handleClosePreview = useCallback(() => {
    setPreviewHtml('')
    setPreviewContractNo('')
  }, [])

  useEffect(() => {
    if (!open) {
      autoPreviewKeyRef.current = ''
      return
    }
    if (!contractNo || autoPreviewKeyRef.current === contractNo) return
    autoPreviewKeyRef.current = contractNo
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await triggerPrint(contractNo).unwrap()
          if (!result.data.html) {
            setErrorMessage('Print preview could not open because the server did not return a document.')
            onClose()
            return
          }
          setPreviewHtml(buildNPEPrintPreviewHtml(result.data.html))
          setPreviewContractNo(contractNo)
          setToastMessage(result.message ?? 'Print preview generated.')
          onClose()
        } catch (err) {
          setErrorMessage(getErrorMessage(err, 'Print NPE Contract failed'))
          onClose()
        }
      })()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [open, contractNo, triggerPrint, onClose])

  return (
    <>
      <Dialog open={open && isFetching && !previewHtml} maxWidth="xs" fullWidth>
        <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 4 }}>
          <CircularProgress size={22} />
          <Typography variant="body2">Generating print preview…</Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(previewHtml)} onClose={handleClosePreview} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box component="span">Print Preview - NPE Contract</Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClosePreview} size="small">Close</Button>
            <Button
              variant="contained"
              size="small"
              disabled={!previewHtml}
              onClick={() => {
                const printTitle = `${sanitizePdfFileNamePart(previewContractNo || 'npe-contract')}_NPE_Contract.pdf`
                void printAndUploadReport({
                  iframeTitle: 'NPE Contract Preview',
                  reportName: 'rpPurchaseQryNPE',
                  printTitle,
                  html: previewHtml,
                  onUploadError: setToastMessage,
                })
              }}
            >
              Print
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title="NPE Contract Preview"
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(errorMessage)} onClose={() => setErrorMessage('')} maxWidth="xs" fullWidth>
        <DialogTitle>Print Error</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">{errorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" size="small" onClick={() => setErrorMessage('')}>OK</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2000}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  )
}

export function NPEContractListPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const { selectedId, toggleSelect } = useRowSelection<number>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const { getWidth, startResize } = useColumnWidths('purchase.npe-contract')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof NPEContractOut> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)
  const closeFilterPopover = () => filterPopover.close()

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof NPEContractOut
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    closeFilterPopover()
  }

  const [createOpen, setCreateOpen] = useState(false)
  const [editingNo, setEditingNo] = useState<string | null>(null)
  const [copyingNo, setCopyingNo] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<NPEContractOut | null>(null)
  const [printTarget, setPrintTarget] = useState<NPEPrintTarget | null>(null)

  const { data, isLoading, isFetching, error } = useListNPEContractsQuery({ page: 1, page_size: ALL_ROWS_PAGE_SIZE }, { refetchOnMountOrArgChange: true })
  const { data: editingDetail } = useGetNPEContractQuery(editingNo ?? '', { skip: !editingNo })
  const { data: copyingDetail } = useGetNPEContractQuery(copyingNo ?? '', { skip: !copyingNo })

  const [deleteCt, { isLoading: deleting }] = useDeleteNPEContractMutation()

  const baseRows = useMemo(() => data?.items ?? [], [data])

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'purchase',
    prefKey: 'columns',
    subKey: 'npe-contract',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'purchase.npe-contract',
  })
  const { visibleColumns, openMenu } = columnsCtrl

  const minTableWidth = useMemo(
    () => Math.max(1200, visibleColumns.length * 140),
    [visibleColumns.length],
  )

  const filteredRows = useMemo(() =>
    baseRows.filter((row) => {
      if (!rowMatchesSearch(row as unknown as Record<string, unknown>, searchTerm, TABLE_COLUMNS as unknown as { key: string }[])) return false
      if (!rowMatchesAllColumnFilters(row as unknown as Record<string, unknown>, columnFilters.filters)) return false
      return true
    }),
    [baseRows, searchTerm, columnFilters.filters],
  )

  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, visibleColumns), [filteredRows, sortState, visibleColumns])
  const rows = useMemo(() => rowsPerPage === -1 ? sortedRows : sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [page, rowsPerPage, sortedRows])

  const vendorOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const r of baseRows) { if (!r.cp_code) continue; if (!seen.has(r.cp_code)) seen.set(r.cp_code, `${r.cp_code}${r.name ? ` — ${r.name}` : ''}`) }
    return [...seen.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [baseRows])
  // Per-column ▽ popover metadata
  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows as never) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows as unknown as Record<string, unknown>[], String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter(
            (col) =>
              col.kind === 'number' &&
              !NON_SUM_TOTAL_KEYS.has(String(col.key)) &&
              !NON_SUM_KEYS_OVERRIDE.has(String(col.key)),
          )
          .map((col) => [String(col.key), sumNumericColumn(filteredRows, col.key)]),
      ),
    [filteredRows, visibleColumns],
  )

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`
  const selectedRow = useMemo(() => baseRows.find((r) => r.consignment_p_id === selectedId) ?? null, [baseRows, selectedId])
  const toggleSort = (key: keyof NPEContractOut) => { setSortState((current) => toggleTableSort(current, key)); setPage(0) }

  const handleEdit = () => { if (!selectedRow) return; setEditingNo(selectedRow.contract_no) }
  const handleCopy = () => { if (!selectedRow) return; setCopyingNo(selectedRow.contract_no) }
  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try { await deleteCt(confirmDelete.contract_no).unwrap(); snack.success(`Deleted NPE Contract ${confirmDelete.contract_no}`); setConfirmDelete(null) }
    catch (err) { snack.error(getErrorMessage(err, 'Delete failed')) }
  }
  const handlePrint = () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    setPrintTarget({ contractNo: selectedRow.contract_no })
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => setCreateOpen(true)}>New</Button>
            <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={handleEdit}>Edit</Button>
            <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={handleCopy}>Copy</Button>
            <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={() => selectedRow && setConfirmDelete(selectedRow)}>Delete</Button>
            <Button variant="outlined" size="small" sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }} disabled={!selectedRow} onClick={handlePrint}>Print</Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button size="small" variant="outlined" endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openMenu(e.currentTarget)} sx={filterBarBtnSx}>Columns</Button>
      </FilterBar>
      <Divider sx={{ mb: 0.5 }} />

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>Total: {filteredRows.length}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>Loaded: {baseRows.length}</Typography>
            <TablePagination component="div" count={filteredRows.length} page={rowsPerPage === -1 ? 0 : page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]} labelRowsPerPage="Rows" sx={{ flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 24, px: 0 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontSize: '0.68rem' }, '& .MuiTablePagination-actions': { transform: 'scale(0.86)', transformOrigin: 'center' } }} />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={360}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="NPE no, vendor..."
              label="Search NPE Contracts"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => { if (!colPopover) return; columnFilters.setFilter(String(colPopover.key), v); setPage(0) }}
          options={popoverOptions}
        />

        <FuturesColumnsMenu controller={columnsCtrl} />

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load NPE Contracts"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No NPE Contracts found"
              description="Try adjusting the search, vendor / certification / date filters above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const w = getWidth(String(col.key))
                    return (
                      <TableCell
                        key={String(col.key)}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', position: 'relative' }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(String(col.key))}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(String(col.key), e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const total = totalRowValues.get(String(col.key))
                    const val = total == null ? '' : formatAccessNumber(total)
                    return (
                      <TableCell
                        key={String(col.key)}
                        align={col.align}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: NPEContractOut) =>
                visibleColumns.map((col) => {
                  const val = cellValue(row, col)
                  const w = getWidth(String(col.key))
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>{val || '-'}</Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const id = Number.isFinite(idx) ? rows[idx]?.consignment_p_id : null
                  return <TableRow {...props} hover selected={id != null && id === selectedId} sx={{ cursor: 'pointer' }} onClick={() => { if (id != null) toggleSelect(id) }} />
                },
              }}
            />
          )}
        </Box>
      </Box>

      <NPEContractFormDialog open={createOpen} mode="create" onClose={() => setCreateOpen(false)} />
      <NPEContractFormDialog open={!!editingNo && !!editingDetail?.data} mode="edit" initialValue={editingDetail?.data ?? null} onClose={() => setEditingNo(null)} />
      <NPEContractFormDialog open={!!copyingNo && !!copyingDetail?.data} mode="copy" initialValue={copyingDetail?.data ?? null} onClose={() => setCopyingNo(null)} />
      <ConfirmDialog open={!!confirmDelete} title="Delete NPE Contract" message={confirmDelete ? `Are you sure you want to delete NPE Contract ${confirmDelete.contract_no}?` : ''} destructive loading={deleting} onConfirm={() => void handleConfirmDelete()} onCancel={() => setConfirmDelete(null)} />
      <NPEContractPrintDialog
        open={Boolean(printTarget)}
        printTarget={printTarget}
        onClose={() => setPrintTarget(null)}
      />
    </Box>
  )
}

export default NPEContractListPage
